/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN STUDENT FEE PLAN THUNK
 * Role: Admin
 * Scope: Async actions for managing student fee plan mapping,
 *        history auditing, and correction workflows.
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getStudentActiveFeePlan,
  getStudentFeePlanHistory,
  createStudentFeePlan,
  updateStudentFeePlan,
  deactivateStudentFeePlan,
} from "../../api/admin/student_fee_plan.api";

/**
 * Thunk: Fetch a student's current active fee plan configuration.
 * 
 * @param {string|number} studentId - Unique identifier of the student
 */
export const fetchStudentActiveFeePlan = createAsyncThunk(
  "admin/studentFeePlan/fetchActive",
  async (studentId, { rejectWithValue }) => {
    try {
      const res = await getStudentActiveFeePlan(studentId);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch active fee plan.");
    }
  }
);

/**
 * Thunk: Fetch the full historical audit trail of fee modifications for a student.
 * 
 * @param {string|number} studentId - Unique identifier of the student
 */
export const fetchStudentFeePlanHistory = createAsyncThunk(
  "admin/studentFeePlan/fetchHistory",
  async (studentId, { rejectWithValue }) => {
    try {
      const res = await getStudentFeePlanHistory(studentId);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch fee plan history.");
    }
  }
);

/**
 * Thunk: Assign a new fee plan or schedule a new period rate (auto-archives previous).
 * 
 * @param {Object} payload - Plan parameters including student_id, amount, billing_cycle, etc.
 */
export const saveStudentFeePlan = createAsyncThunk(
  "admin/studentFeePlan/save",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await createStudentFeePlan(payload);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return res;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to map fee plan.");
    }
  }
);

/**
 * Thunk: Update or correct an existing student fee plan record (active or historical).
 * Fully optimized to extract record_id/planId flexibly from the dispatched argument payload.
 * 
 * @param {Object} params - Contains planId (or record_id) and the updated payload properties
 */
export const updateStudentFeePlanThunk = createAsyncThunk(
  "admin/studentFeePlan/update",
  async ({ record_id, plan_id, student_id, ...payload }, { rejectWithValue }) => {
    try {
      // Fallback selector for identifier to robustly support various payload structures
      const targetId = record_id || plan_id || payload.id;

      if (!targetId) {
        return rejectWithValue("Missing plan record ID for correction update.");
      }

      const res = await updateStudentFeePlan(targetId, { student_id, ...payload });

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return res;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update fee plan record.");
    }
  }
);

/**
 * Thunk: Deactivate an active or specific student fee plan record.
 * 
 * @param {string|number} planId - The unique identifier of the fee plan entry
 */
export const removeStudentFeePlan = createAsyncThunk(
  "admin/studentFeePlan/deactivate",
  async (planId, { rejectWithValue }) => {
    try {
      const res = await deactivateStudentFeePlan(planId);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return { planId };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to deactivate fee plan.");
    }
  }
);